import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { HelpChat } from "../components/HelpChat";

const strategies = [
  {
    name: "TC-03: Multi-Timeframe Trend Rotation",
    img: "/strategy-1.png",
    cagr: "81.9%",
    sharpe: "3.50",
    maxDrawdown: "7.7%",
    backtestId: "a1b2c3d4-0003-4000-8000-000000000003",
  },
  {
    name: "TC-02: Adaptive VIX Scale-In",
    img: "/strategy-2.png",
    cagr: "76.9%",
    sharpe: "4.05",
    maxDrawdown: "7.1%",
    backtestId: "a1b2c3d4-0002-4000-8000-000000000002",
  },
  {
    name: "TC-09: Policy Hedge Diversifier",
    img: "/strategy-3.png",
    cagr: "50.5%",
    sharpe: "2.68",
    maxDrawdown: "9.8%",
    backtestId: "a1b2c3d4-0009-4000-8000-000000000009",
  },
  {
    name: "TC-05: Precision Alpha",
    img: "/strategy-4.png",
    cagr: "60.1%",
    sharpe: "3.61",
    maxDrawdown: "5.3%",
    backtestId: "a1b2c3d4-0005-4000-8000-000000000005",
  },
];

export function IntroPage() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const prevTheme = useRef<string | null>(null);

  // Force arctic (light) theme on this page — restore on unmount
  useEffect(() => {
    prevTheme.current = document.documentElement.getAttribute("data-theme");
    document.documentElement.setAttribute("data-theme", "arctic");
    return () => {
      if (prevTheme.current) {
        document.documentElement.setAttribute("data-theme", prevTheme.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="landing-page min-h-screen bg-surface-950 text-surface-100 overflow-x-hidden">
      {/* Nav */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-surface-950/95 backdrop-blur-xl border-b border-surface-800/50 shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="TradeCraft" className="w-10 h-10" />
              <div className="hidden sm:block">
                <span className="text-xl font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                  TradeCraft
                </span>
                <p className="text-xs text-surface-400">by Colvix</p>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-surface-300 hover:text-primary-400 transition-colors">Features</a>
              <a href="#strategies" className="text-surface-300 hover:text-primary-400 transition-colors">Strategies</a>
              <a href="#pricing" className="text-surface-300 hover:text-primary-400 transition-colors">Pricing</a>
              <a href="#how-it-works" className="text-surface-300 hover:text-primary-400 transition-colors">How It Works</a>
              <a href="#faq" className="text-surface-300 hover:text-primary-400 transition-colors">FAQ</a>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/login")}
                className="hidden sm:block px-4 py-2 text-sm font-medium text-surface-300 hover:text-surface-100 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                Get Started Free
              </button>
              {/* Hamburger — mobile only */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-surface-300 hover:text-surface-100 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-surface-950/95 backdrop-blur-xl border-t border-surface-800/50">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
              {["features", "strategies", "pricing", "how-it-works", "faq"].map((id) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg text-surface-300 hover:text-primary-400 hover:bg-surface-800/50 transition-colors capitalize"
                >
                  {id.replace(/-/g, " ")}
                </a>
              ))}
              <hr className="border-surface-800/50 my-1" />
              <button
                onClick={() => { setMobileMenuOpen(false); navigate("/login"); }}
                className="px-4 py-3 rounded-lg text-surface-300 hover:text-surface-100 hover:bg-surface-800/50 transition-colors text-left"
              >
                Sign In
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate("/signup"); }}
                className="mt-1 px-4 py-3 text-center font-semibold rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all"
              >
                Sign Up Free →
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center pt-20 pb-16 sm:pb-24 overflow-hidden">
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="/intro.mp4?v=2" type="video/mp4" />
        </video>
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-surface-950/75 z-[1]" />

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
          <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-primary-500/5 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-1/4 -right-1/4 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: "1s" }} />
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:60px_60px] z-[2]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-800/50 border border-surface-700/50 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm text-surface-300">Powered by Alpaca & Charles Schwab</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 animate-fade-in" style={{ animationDelay: "100ms" }}>
            <span className="bg-gradient-to-r from-primary-400 via-accent-400 to-primary-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              Automated Trading
            </span>
            <br />
            <span className="text-surface-100">Made Simple</span>
          </h1>
          <p className="text-lg sm:text-xl text-surface-400 max-w-3xl mx-auto mb-10 animate-fade-in" style={{ animationDelay: "200ms" }}>
            Pick a strategy, connect your brokerage, let it trade for you.
            Your money stays in <strong className="text-surface-200">your</strong> Alpaca account — always.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in" style={{ animationDelay: "300ms" }}>
            <button
              onClick={() => navigate("/signup")}
              className="group px-8 py-4 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-2xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-1 flex items-center gap-3"
            >
              Sign Up Free
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            <button
              onClick={() => navigate("/search")}
              className="px-8 py-4 text-lg font-semibold rounded-xl bg-surface-800/50 border border-surface-700 text-surface-100 hover:bg-surface-700/50 hover:border-surface-600 transition-all duration-300 flex items-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Community Strategies
            </button>
          </div>
        </div>
      </section>

      {/* Platform Demo Video */}
      <section className="py-16 sm:py-24 bg-surface-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-surface-100 mb-3">
              See It In Action
            </h2>
            <p className="text-surface-400">A quick look at how TradeCraft works.</p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-surface-700/50 shadow-2xl">
            <video
              controls
              playsInline
              className="w-full"
            >
              <source src="/TradeCraft.mp4?v=2" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Common Question Callout */}
      <section className="relative py-16 sm:py-20 bg-surface-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">
            <span style={{ color: "#22c55e", display: "inline" }}>Do I need to know how to build algo strategies to use this platform?</span>
          </p>
          <p className="text-lg sm:text-xl text-surface-300 leading-relaxed">
            <strong className="text-surface-100">No.</strong> There are community-built strategies that perform — you can invest in those right away.
            If you'd like to build your own custom strategy, the platform allows that too.
            <br className="hidden sm:block" />
            <span className="text-surface-400 mt-2 inline-block">Pick a proven strategy or create your own — it's up to you.</span>
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-24 sm:py-32 bg-gradient-to-b from-surface-950 to-surface-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
              Why TradeCraft
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              Invest Smarter,
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                Not Harder
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: (
                  <svg className="w-7 h-7 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
                title: "Community Strategies",
                desc: "Browse strategies built by others. Pick what fits your risk tolerance.",
                gradient: "bg-gradient-to-br from-blue-500 to-cyan-500",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                title: "Hands-Off Trading",
                desc: "Strategies rebalance your portfolio automatically. No daily monitoring needed.",
                gradient: "bg-gradient-to-br from-purple-500 to-pink-500",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                ),
                title: "Your Money, Your Control",
                desc: "Funds stay in your Alpaca brokerage. Add, withdraw, or liquidate same day.",
                gradient: "bg-gradient-to-br from-teal-500 to-cyan-500",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: "Backtested Results",
                desc: "Every strategy is tested against years of real market data before you invest.",
                gradient: "bg-gradient-to-br from-green-500 to-emerald-500",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
                title: "Rich Analytics",
                desc: "Sharpe ratio, max drawdown, alpha, beta — the full picture at a glance.",
                gradient: "bg-gradient-to-br from-indigo-500 to-violet-500",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: "Commission-Free",
                desc: "Trade US stocks & ETFs with zero commissions through Alpaca.",
                gradient: "bg-gradient-to-br from-orange-500 to-red-500",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
                title: "Portfolio Analyzer",
                desc: "Correlation matrix, Sharpe optimization, and Monte Carlo with 5,000 paths — all in-browser, under 100ms.",
                gradient: "bg-gradient-to-br from-cyan-500 to-blue-500",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                ),
                title: "Diversifier",
                desc: "Combine strategies, set weights, see best/avg/worst projections vs S&P 500.",
                gradient: "bg-gradient-to-br from-emerald-500 to-teal-500",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                ),
                title: "TradeCraft Pulse",
                desc: "Live market dashboard — indices, sector heatmap, sentiment, crypto, news. Auto-refreshes every 30s.",
                gradient: "bg-gradient-to-br from-amber-500 to-orange-500",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                ),
                title: "Mirror Trading — Schwab",
                desc: "Auto-replicate every Alpaca trade to Charles Schwab in real time. One toggle.",
                gradient: "bg-gradient-to-br from-rose-500 to-pink-500",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="group relative p-6 sm:p-8 rounded-2xl bg-surface-900/80 border border-surface-700/50 hover:border-primary-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary-500/10 overflow-hidden"
              >
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 ${item.gradient}`} />
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-surface-100 mb-3">{item.title}</h3>
                  <p className="text-surface-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategy Showcase */}
      <section id="strategies" className="relative py-24 sm:py-32 bg-surface-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium mb-4">
              Real Backtests
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              Strategies That
              <br />
              <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                Perform
              </span>
            </h2>
            <p className="text-lg text-surface-400 max-w-2xl mx-auto">
              Every strategy is backtested against 10 years of real historical data. See the results yourself.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {strategies.map((s) => (
              <div
                key={s.name}
                className="group relative rounded-2xl overflow-hidden border-2 border-surface-600/30 hover:border-primary-500/50 bg-surface-950 transition-all duration-300 hover:shadow-xl cursor-pointer shadow-md"
                onClick={() => navigate(`/backtest/${s.backtestId}`)}
              >
                <div className="relative">
                  <img
                    src={s.img}
                    alt={s.name}
                    className="w-full h-48 sm:h-56 object-cover object-top"
                  />
                </div>
                <div className="p-5 border-t border-surface-700/50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-surface-100 group-hover:text-primary-400 transition-colors">
                        {s.name}
                      </h3>
                      <p className="text-xs text-surface-500 mt-0.5">3-year backtest · Click to view →</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-surface-500 uppercase tracking-wider">CAGR</p>
                      <p className="text-2xl font-bold"><span style={{ color: "#22c55e", display: "inline" }}>{s.cagr}</span></p>
                    </div>
                  </div>
                  <div className="flex gap-4 pt-2 border-t border-surface-800/50">
                    <div>
                      <p className="text-xs text-surface-500 uppercase tracking-wider">Sharpe</p>
                      <p className="text-sm font-semibold text-surface-200">{s.sharpe}</p>
                    </div>
                    <div>
                      <p className="text-xs text-surface-500 uppercase tracking-wider">Max DD</p>
                      <p className="text-sm font-semibold text-red-400">{s.maxDrawdown}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative py-24 sm:py-32 bg-gradient-to-b from-surface-900 to-surface-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 text-accent-400 text-sm font-medium mb-4">
              Simple Process
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              Get Started
              <br />
              <span className="bg-gradient-to-r from-accent-400 to-primary-400 bg-clip-text text-transparent">
                In Three Steps
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12 lg:gap-16 relative">
            <div className="hidden md:block absolute top-8 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-primary-500/20 via-accent-500/40 to-primary-500/20" />

            {[
              {
                num: 1,
                title: "Sign Up Free",
                desc: "Create your account in seconds. No credit card needed to explore strategies and backtests.",
              },
              {
                num: 2,
                title: "Subscribe — $250/year",
                desc: "One simple annual subscription. No hidden fees, no additional charges. Unlock automated trading and all platform features.",
              },
              {
                num: 3,
                title: "Connect & Trade",
                desc: "Link your Alpaca brokerage, pick strategies, and the system auto-trades daily at 3:40 PM ET on your behalf.",
              },
            ].map((step) => (
              <div key={step.num} className="relative flex flex-col items-center text-center group">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-2xl font-bold text-white mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-primary-500/30">
                  {step.num}
                </div>
                <h3 className="text-lg font-semibold text-surface-100 mb-2">{step.title}</h3>
                <p className="text-surface-400 text-sm leading-relaxed max-w-xs">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative py-24 sm:py-32 bg-surface-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
              Simple Pricing
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              One Plan,
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                Everything Included
              </span>
            </h2>
            <p className="text-lg text-surface-400">
              No hidden costs. No additional charges. No surprises.
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <div className="relative rounded-2xl border-2 border-primary-500/50 bg-surface-900/80 p-8 sm:p-10 shadow-2xl shadow-primary-500/10">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 text-white text-sm font-semibold">
                  Annual Subscription
                </span>
              </div>

              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-5xl sm:text-6xl font-bold" style={{ color: "#f1f5f9" }}>$250</span>
                  <span className="text-xl" style={{ color: "#94a3b8" }}>/ year</span>
                </div>
                <p className="text-surface-400">That's less than $21/month for fully automated trading</p>
              </div>

              <div className="space-y-3 mb-8">
                {[
                  "Unlimited backtesting 24/7",
                  "Unlimited access to community strategies",
                  "Strategy Builder — build your own with all NYSE stocks",
                  "Automated daily execution at 3:40 PM ET",
                  "Paper & live trading via Alpaca",
                  "Mirror Trading to Charles Schwab",
                  "Portfolio Analyzer — correlation, Monte Carlo, Sharpe optimization",
                  "Diversifier — multi-strategy modeling with scenario projections",
                  "TradeCraft Pulse — live market dashboard",
                  "Real-time market data",
                  "Portfolio performance vs SPY benchmark",
                  "Full execution logs — every trade, every signal",
                  "Backtest vs live comparison",
                  "Full strategy control — add, withdraw, or liquidate anytime",
                  "Brokerage performance dashboard",
                  "Live strategy updates in real-time",
                ].map((feature) => (
                  <div key={feature} className="flex items-start gap-3">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#22c55e" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm" style={{ color: "#cbd5e1" }}>{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate("/signup")}
                className="w-full px-8 py-4 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-2xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                Sign Up Free & Subscribe
              </button>
              <p className="text-center text-xs text-surface-500 mt-3">Secure payment via Stripe. Cancel anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — Architecture Diagram */}
      <section id="how-it-works-diagram" className="relative py-24 sm:py-32 bg-surface-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
              Architecture
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              How It{" "}
              <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                All Connects
              </span>
            </h2>
            <div className="mx-auto max-w-2xl mt-2 px-6 py-5 rounded-2xl border-2 border-green-500/40 bg-green-950/30 text-center">
              <span className="text-5xl">🏦</span>
              <p className="text-2xl sm:text-3xl font-bold mt-3"><span style={{ color: "#22c55e", display: "inline" }}>Your money stays in your Bank account.</span></p>
              <p className="text-base text-surface-300 mt-2">TradeCraft just runs the strategies — it never holds your funds.</p>
            </div>
          </div>

          {/* Diagram */}
          <div className="relative">
            {/* Row 1 — Subscribers */}
            <div className="flex justify-center gap-4 sm:gap-6 mb-4">
              {[
                { emoji: "👩🏻", name: "Sarah" },
                { emoji: "👨🏽", name: "Mike" },
                { emoji: "👩🏾", name: "Priya" },
                { emoji: "👨🏼", name: "James" },
              ].map((u) => (
                <div
                  key={u.name}
                  className="flex flex-col items-center gap-1 px-4 py-3 rounded-xl bg-[#4338ca] border-2 border-indigo-400 min-w-[80px] shadow-lg shadow-indigo-500/20"
                >
                  <span className="text-2xl">{u.emoji}</span>
                  <span className="text-sm font-medium text-indigo-200">{u.name}</span>
                </div>
              ))}
            </div>

            {/* Arrow down */}
            <div className="flex justify-center mb-4">
              <div className="flex flex-col items-center">
                <div className="w-0.5 h-8 bg-indigo-500" />
                <span className="text-xs text-indigo-400 font-medium">subscribe</span>
                <svg className="w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Row 2 — TradeCraft Platform */}
            <div className="mx-auto max-w-lg mb-4">
              <div className="text-center px-6 py-4 rounded-xl bg-[#1e1b4b] border-2 border-indigo-500 shadow-lg shadow-indigo-500/10">
                <span className="text-xl">⚡</span>
                <h3 className="text-lg font-bold text-indigo-100 mt-1">TradeCraft Platform</h3>
                <p className="text-sm text-indigo-300">$250/year flat · No % cut · No hidden fees</p>
              </div>
            </div>

            {/* Arrow down */}
            <div className="flex justify-center mb-4">
              <div className="w-0.5 h-8 bg-indigo-500" />
            </div>

            {/* Row 3 — Strategies */}
            <div className="flex justify-center gap-4 sm:gap-6 mb-4">
              <div className="px-5 py-3 rounded-xl bg-[#1e1b4b] border border-[#818cf8] text-center">
                <span className="text-lg">🌍</span>
                <p className="text-sm font-semibold text-indigo-200 mt-1">Community Strategies</p>
                <p className="text-xs text-indigo-400">CFR · VIX · Tariff · HWR</p>
              </div>
              <div className="px-5 py-3 rounded-xl bg-[#1e1b4b] border border-[#818cf8] text-center">
                <span className="text-lg">🛠️</span>
                <p className="text-sm font-semibold text-indigo-200 mt-1">Build Your Own</p>
                <p className="text-xs text-indigo-400">Custom logic · Your rules</p>
              </div>
            </div>

            {/* Arrow down */}
            <div className="flex justify-center mb-4">
              <div className="w-0.5 h-8 bg-purple-500" />
            </div>

            {/* Row 4 — Engine */}
            <div className="mx-auto max-w-md mb-4">
              <div className="text-center px-6 py-4 rounded-xl bg-[#312e81] border-2 border-purple-400 shadow-lg shadow-purple-500/10">
                <span className="text-xl">🔄</span>
                <h3 className="text-lg font-bold text-indigo-100 mt-1">Trading Engine</h3>
                <p className="text-sm text-indigo-300">Runs daily at 3:40 PM ET</p>
              </div>
            </div>

            {/* Arrow down — thick green */}
            <div className="flex justify-center mb-4">
              <div className="flex flex-col items-center">
                <div className="w-1 h-8 bg-green-500" />
                <span className="text-xs text-green-400 font-medium">API trades</span>
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Row 5 — Bank accounts */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
              {[
                { name: "Sarah", cash: "$24,800", stocks: "TQQQ · SOXL · UPRO" },
                { name: "Mike", cash: "$5,200", stocks: "QQQ · SPY · AAPL" },
                { name: "Priya", cash: "$41,000", stocks: "NVDA · MSFT · AMZN" },
                { name: "James", cash: "$12,500", stocks: "AAPL · GOOGL · META" },
              ].map((b) => (
                <div
                  key={b.name}
                  className="px-4 py-4 rounded-xl bg-[#052e16] border-2 border-green-500/60 text-center"
                >
                  <span className="text-3xl">🏦</span>
                  <p className="text-sm font-bold text-green-100 mt-2">{b.name}'s Bank Account</p>
                  <p className="text-xs text-green-400 mt-1">💵 {b.cash}</p>
                  <p className="text-xs text-green-400">📈 {b.stocks}</p>
                </div>
              ))}
            </div>

            {/* Arrow down */}
            <div className="flex justify-center mb-4">
              <div className="w-0.5 h-8 bg-cyan-500" />
            </div>

            {/* Row 6 — Controls */}
            <div className="mx-auto max-w-xl mb-4">
              <div className="text-center px-6 py-4 rounded-xl bg-[#164e63] border-2 border-cyan-400 shadow-lg shadow-cyan-500/20">
                <span className="text-lg">🎮</span>
                <p className="text-sm font-bold text-white mt-1">Subscriber Controls</p>
                <p className="text-xs text-cyan-100 mt-1">➕ Add Funds · 💸 Withdraw · 🔴 Liquidate · 🔀 Switch Strategy</p>
              </div>
            </div>

            {/* Arrow down */}
            <div className="flex justify-center mb-4">
              <div className="w-0.5 h-8 bg-green-400" />
            </div>

            {/* Row 7 — Key Message */}
            <div className="mx-auto max-w-xl mb-8">
              <div className="text-center px-6 py-5 rounded-2xl bg-[#14532d] border-2 border-green-400 shadow-lg shadow-green-500/10">
                <span className="text-2xl">🛡️</span>
                <p className="text-base font-bold text-green-100 mt-2">YOUR MONEY NEVER TOUCHES TRADECRAFT</p>
                <p className="text-sm text-green-300 mt-1">
                  Your cash and stocks stay in <strong className="text-green-100">YOUR</strong> Alpaca brokerage account.
                  <br />Full control · Full visibility · Always yours.
                </p>
              </div>
            </div>

            {/* Algo snippet — side callout */}
            <div className="mx-auto max-w-lg">
              <div className="px-5 py-4 rounded-xl bg-[#1c1917] border border-amber-500/60">
                <p className="text-sm font-semibold text-amber-200 mb-2">📝 What Is a Strategy?</p>
                <pre className="text-xs text-amber-100/90 font-mono leading-relaxed whitespace-pre-wrap">
{`function rebalance(portfolio, market):
  if VIX > 30 and QQQ fell 3%:
    → BUY TQQQ 40% · SOXL 30%
    → HOLD cash 30%
  elif portfolio up 15%:
    → SELL TQQQ · ROTATE → SPY
  else:
    → HOLD — no trades today`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="relative py-24 sm:py-32 bg-gradient-to-b from-surface-900 to-surface-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 text-accent-400 text-sm font-medium mb-4">
              Common Questions
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              Frequently Asked
              <br />
              <span className="bg-gradient-to-r from-accent-400 to-primary-400 bg-clip-text text-transparent">
                Questions
              </span>
            </h2>
          </div>

          <div className="space-y-3">
            {[
              {
                q: "What is a strategy?",
                a: "A strategy is a set of rules that defines how your portfolio should be allocated across different stocks and ETFs. Think of it as a recipe — it specifies what to buy, how much, and when to rebalance. Once you allocate to a strategy, the system automatically executes trades in your brokerage account every trading day at 3:40 PM ET. You don't need to do anything — it's completely hands-off.",
              },
              {
                q: "Is my money safe?",
                a: "Your money never touches TradeCraft. It stays in your own brokerage account at all times. Alpaca is a registered broker-dealer and member of FINRA/SIPC. You have full visibility and control through your brokerage dashboard. You can add funds, withdraw, or liquidate any position at any time.",
              },
              {
                q: "Which brokerages are supported?",
                a: "TradeCraft supports Alpaca for automated strategy execution (paper and live trading) and Charles Schwab via Mirror Trading. Every trade executed on Alpaca is automatically replicated to your Schwab account in real time — same ticker, same quantity, same direction.",
              },
              {
                q: "What is the Portfolio Analyzer?",
                a: (<>A quantitative tool that computes correlation matrices across your selected strategies, runs Sharpe ratio optimization for weight allocation, and performs Monte Carlo stress testing with 5,000 simulated paths. Three optimization modes: Max Sharpe, Max CAGR, and Min Drawdown. All runs in-browser — no server needed, results in under 100ms. <a href="/walkthroughs/PSPH4wTB21A" className="text-primary-400 hover:text-primary-300 underline">Watch walkthrough →</a></>),
              },
              {
                q: "What is the Diversifier?",
                a: (<>The Diversifier lets you combine multiple strategies into one portfolio, assign custom weight allocations, and see projected performance across best case, average, and worst case scenarios — all benchmarked against the S&P 500. It shows how diversification lowers drawdowns and smooths returns. <a href="/walkthroughs/3WBjn27YM6Y" className="text-primary-400 hover:text-primary-300 underline">Watch walkthrough →</a></>),
              },
              {
                q: "What is TradeCraft Pulse?",
                a: (<>A live market dashboard with major indices, futures, interactive charts, sector heatmaps, Fear & Greed index, top movers, real-time news, treasury yields, crypto, social sentiment, and an earnings calendar. Market data refreshes every 30 seconds. <a href="/walkthroughs/90dJ2Wk8RMU" className="text-primary-400 hover:text-primary-300 underline">Watch walkthrough →</a></>),
              },
              {
                q: "What is Mirror Trading?",
                a: (<>Mirror Trading auto-replicates every trade from your Alpaca account to your Charles Schwab account in real time. Enable it with a single toggle after connecting your Schwab API credentials. Same ticker, same quantity, same direction — no manual effort. <a href="/walkthroughs/1pTTITg39-k" className="text-primary-400 hover:text-primary-300 underline">Watch walkthrough →</a></>),
              },
              {
                q: "Do I need coding experience?",
                a: "No. You can browse community strategies and invest with one click. If you want to build your own, the Visual Strategy Builder lets you define rules without writing code. Power users can also write strategies directly in the TradeCraft DSL.",
              },
              {
                q: "Can I withdraw my money anytime?",
                a: "Yes. Since your funds are in your own brokerage account, you can withdraw at any time. You can also liquidate any strategy allocation same day — you're always in control.",
              },
              {
                q: "What happens if a strategy loses money?",
                a: "All investing involves risk, including the possible loss of principal. Past backtest performance does not guarantee future results. You should only invest money you can afford to lose. TradeCraft provides the tools and data — the investment decision is always yours.",
              },
              {
                q: "Can I run multiple strategies at once?",
                a: "Yes. You can invest in multiple strategies simultaneously. The Portfolio Analyzer and Diversifier are specifically designed to help you evaluate and manage multi-strategy portfolios.",
              },
              {
                q: "What does the subscription cost?",
                a: "$250 per year. That's it — no hidden fees, no commissions, no percentage of profits. Alpaca also provides commission-free trading for US equities.",
              },
              {
                q: "Do I need to monitor my trades daily?",
                a: "No. Once you allocate to a strategy, the system handles everything automatically. You get full execution logs showing every trade and the signal that triggered it. Check anytime, but no daily monitoring is required.",
              },
              {
                q: "Can I cancel my subscription?",
                a: "Yes. You can cancel anytime from your account settings. Your subscription remains active until the end of the current billing period. Your brokerage account and funds are unaffected — they're yours.",
              },
            ].map((faq, i) => (
              <details key={i} className="group rounded-xl border border-surface-700/50 bg-surface-900/80 overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-surface-700/50 transition-colors list-none">
                  <span className="text-sm font-medium pr-4" style={{ color: "#f1f5f9" }}>{faq.q}</span>
                  <svg className="w-5 h-5 text-surface-400 flex-shrink-0 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-4 text-sm leading-relaxed border-t border-surface-700/30 pt-3" style={{ color: "#cbd5e1" }}>
                  {faq.a}
                </div>
              </details>
            ))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => navigate("/faq")}
              className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              View all FAQs →
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-surface-950 to-accent-500/10" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.05)_1px,transparent_1px)] bg-[size:40px_40px]" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-6">
            Ready to
            <br />
            <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
              Get Started?
            </span>
          </h2>
          <p className="text-lg text-surface-400 mb-10 max-w-2xl mx-auto">
            Create your free account and start exploring strategies today. Questions? Reach us at{" "}
            <a href="mailto:info@colvix.net" className="text-primary-400 hover:underline">info@colvix.net</a>
          </p>
          <button
            onClick={() => navigate("/signup")}
            className="group px-8 py-4 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-2xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-1 flex items-center gap-3 mx-auto"
          >
            Create Free Account
            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>
      </section>

      {/* Disclaimer */}
      <section style={{ background: "#0f172a" }} className="border-t border-surface-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="border-t border-surface-800/50 pt-8 mb-8">
            <div className="space-y-3 text-xs leading-relaxed" style={{ color: "#94a3b8" }}>
              <p>
                <strong style={{ color: "#f1f5f9" }}>Important Disclaimer:</strong> Colvix LLC provides TradeCraft as a platform for algorithmic strategy development and rule-based trading research.
                <strong style={{ color: "#f1f5f9" }}> We are not a broker, investment advisor, or fiduciary.</strong> Our platform is designed to help you make informed, independent investment decisions.
              </p>
              <p>
                All data, analyses, backtests, metrics, signals, and platform features are provided "AS-IS" without any warranties or guarantees.
                <strong style={{ color: "#f1f5f9" }}> Past performance is not indicative of future results.</strong> Investing involves risk, including the possible loss of principal.
                We do not guarantee performance and bear no responsibility for your trading activities or investment decisions.
              </p>
              <p>
                We strongly advise exercising sound judgment and conducting comprehensive research and due diligence prior to trading or investing.
                This is not an offer, solicitation of an offer, or advice to buy or sell securities.
              </p>
              <p>
                Trademarks and logos are the property of their respective owners and do not represent endorsements of any kind.
                Unless otherwise noted, Colvix LLC and its affiliates are not partners, affiliates, or licensees of these companies.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs" style={{ color: "#94a3b8" }}>
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="TradeCraft" className="w-8 h-8" />
              <div>
                <span>© {new Date().getFullYear()} TradeCraft by Colvix LLC. All rights reserved.</span>
                <p className="mt-0.5" style={{ color: "#64748b" }}>Built by ex-Wall Street engineers and traders in Maryland.</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/terms")} className="hover:text-white transition-colors">Terms</button>
              <button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy</button>
              <button onClick={() => navigate("/faq")} className="hover:text-white transition-colors">FAQ</button>
              <a href="mailto:info@colvix.net" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </section>
      <HelpChat />
    </div>
  );
}
